// Get runtime configuration - using process.env directly as publicRuntimeConfig is deprecated
// const { publicRuntimeConfig } = getConfig() || {
//   publicRuntimeConfig: {},
// };
const publicRuntimeConfig = {
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
  NEXT_PUBLIC_ADMIN_EMAIL: process.env.NEXT_PUBLIC_ADMIN_EMAIL,
};

/**
 * Centralized API Configuration Service
 *
 * Goals:
 * 1) Use **relative URLs** (`/api/v1`) everywhere on remote clusters (any non-localhost host).
 * 2) In local development, always prefer **relative URLs** so requests are proxied
 *    via Next.js rewrites (same-origin → no CORS), even if NEXT_PUBLIC_API_URL is set
 *    to a remote backend. This fixes the “credentials + wildcard” CORS error.
 * 3) Optional escape hatch: set `NEXT_FORCE_ABSOLUTE_API_URL=1` to force using the absolute URL.
 */
class ApiConfigService {
  private static _instance: ApiConfigService;
  private _baseUrl: string | null = null;
  private _isInitialized = false;

  private constructor() {}

  static getInstance(): ApiConfigService {
    if (!ApiConfigService._instance) {
      ApiConfigService._instance = new ApiConfigService();
    }
    return ApiConfigService._instance;
  }

  /**
   * API URL resolution with proxy support for CORS handling
   * Priority:
   * 1. Force absolute if explicitly requested via NEXT_FORCE_ABSOLUTE_API_URL=1 and NEXT_PUBLIC_API_URL is set.
   * 2. Otherwise, ALWAYS return a relative base `/api/v1` both in dev and on remote clusters.
   *    (dev uses Next.js rewrites → proxy; remote clusters are same-origin)
   */
  get baseUrl(): string {
    if (this._baseUrl && this._isInitialized) return this._baseUrl;

    let apiUrl = this.resolveApiUrl();
    apiUrl = this.applySecurityFixes(apiUrl);
    this.validateApiUrl(apiUrl);

    this._baseUrl = apiUrl;
    this._isInitialized = true;
    return apiUrl;
  }

  /**
   * Resolve API URL based on environment and configuration
   */
  private resolveApiUrl(): string {
    const forceAbsolute =
      (process.env.NEXT_FORCE_ABSOLUTE_API_URL === "1" ||
        process.env.NEXT_FORCE_ABSOLUTE_API_URL === "true") &&
      !!process.env.NEXT_PUBLIC_API_URL;

    if (forceAbsolute) {
      const explicit = process.env.NEXT_PUBLIC_API_URL!;
      return normalizeAbsoluteApiBase(explicit); // normalized to include /api/v1
    }

    // Default path for both dev and prod: use relative and let
    // - dev: Next.js rewrites proxy to the chosen backend (no CORS)
    // - prod/remote: same-origin requests to the cluster's backend
    if (typeof window !== "undefined") {
      return this.getClientSideUrl();
    } else {
      return this.getServerSideUrl();
    }
  }

  /**
   * Get client-side URL (browser)
   */
  private getClientSideUrl(): string {
    // If we are on localhost (true dev) or any remote host, use relative.
    // This ensures no CORS issues (proxy in dev; same-origin in remote).
    return "/api/v1";
  }

  /**
   * Get server-side URL (SSR/Node)
   */
  private getServerSideUrl(): string {
    // Same logic server-side: stick to relative so requests are same-origin.
    return "/api/v1";
  }

  /**
   * Apply security fixes (Mixed Content, etc.)
   */
  private applySecurityFixes(apiUrl: string): string {
    // Mixed Content Security Fix: Auto-correct HTTP to HTTPS when frontend is served over HTTPS
    if (typeof window !== "undefined") {
      const isCurrentPageHttps = window.location.protocol === "https:";
      const isApiUrlHttp = apiUrl.startsWith("http://");

      if (isCurrentPageHttps && isApiUrlHttp) {
        const httpsUrl = apiUrl.replace(/^http:\/\//, "https://");
        // eslint-disable-next-line no-console
        console.warn(
          `🔒 Mixed Content Fix: Converted API URL from HTTP to HTTPS: ${httpsUrl}`
        );
        return httpsUrl;
      }
    }
    return apiUrl;
  }

  /**
   * Validate the resolved API URL
   */
  private validateApiUrl(apiUrl: string): void {
    if (!apiUrl || (!apiUrl.startsWith("http") && !apiUrl.startsWith("/"))) {
      const error = `❌ API_URL is not configured or invalid. Current value: ${apiUrl}`;
      // eslint-disable-next-line no-console
      console.error(error);

      if (process.env.NODE_ENV === "production") {
        throw new Error(`Invalid API configuration: ${error}`);
      }
    }
  }

  /**
   * Get full URL for an endpoint
   */
  getEndpointUrl(endpoint: string): string {
    return joinUrl(this.baseUrl, endpoint);
  }

  /**
   * Get streaming endpoint URL (SSE)
   * SSE works with relative paths the same way as normal endpoints.
   */
  getStreamingEndpointUrl(endpoint: string = ""): string {
    // In development, use our custom Next.js Route Handler proxy
    // to bypass the default 30s timeout of Next.js rewrites/Webpack dev server.
    // The proxy handler (app/api/stream-proxy) has a manually increased fetch timeout.
    if (process.env.NODE_ENV !== "production") {
      // e.g. "agents/123/sessions/456/messages" -> "/api/stream-proxy/agents/123/sessions/456/messages"
      // Remove leading slash to join cleanly
      const cleanEndpoint = endpoint.replace(/^\//, "");
      return `/api/stream-proxy/${cleanEndpoint}`;
    }

    if (!endpoint) return this.baseUrl;
    return this.getEndpointUrl(endpoint);
  }

  /**
   * Refresh configuration (useful for testing or runtime changes)
   */
  refreshBaseUrl(): void {
    this._baseUrl = null;
    this._isInitialized = false;
  }

  /**
   * Debug information about current configuration
   */
  getDebugInfo() {
    const explicit = process.env.NEXT_PUBLIC_API_URL || "";

    return {
      baseUrl: this._baseUrl,
      isInitialized: this._isInitialized,
      environment: process.env.NODE_ENV,
      isClient: typeof window !== "undefined",
      hostname:
        typeof window !== "undefined" ? window.location.hostname : "server",
      protocol:
        typeof window !== "undefined" ? window.location.protocol : "server",
      explicitApiUrl: explicit,
      forceAbsolute:
        process.env.NEXT_FORCE_ABSOLUTE_API_URL === "1" ||
        process.env.NEXT_FORCE_ABSOLUTE_API_URL === "true",
      proxyEnabled:
        // proxy is "enabled" conceptually whenever we are using a relative base
        typeof this._baseUrl === "string"
          ? this._baseUrl.startsWith("/")
          : true,
    };
  }
}

// --------- helpers ---------

function isLocalhost(hostname: string) {
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1"
  );
}

/**
 * Given any explicit URL (with or without /api/v1), normalize it so the base
 * **includes** /api/v1 exactly once and has no trailing slash.
 * Example:
 *  - http://localhost:8000          -> http://localhost:8000/api/v1
 *  - https://dev.example.com/api/v1 -> https://dev.example.com/api/v1
 */
function normalizeAbsoluteApiBase(url: string): string {
  if (!url) return "/api/v1";
  // strip trailing slash
  let u = url.replace(/\/+$/g, "");
  // strip a single trailing /api/v1 (with optional trailing slash)
  u = u.replace(/\/api\/v1\/?$/i, "");
  return `${u}/api/v1`;
}

/**
 * Join two URL parts with a single slash, preserving protocol and avoiding
 * double slashes.
 */
function joinUrl(base: string, endpoint: string): string {
  const b = base.replace(/\/+$/g, "");
  const e = endpoint.replace(/^\/+/g, "");
  return `${b}/${e}`;
}

// Create singleton instance
const apiConfig = ApiConfigService.getInstance();

/**
 * Application configuration that supports runtime injection from vault
 */
export const appConfig = {
  // API Configuration - centralized through ApiConfigService
  get apiUrl() {
    return apiConfig.baseUrl;
  },

  // External Service Keys
  google: {
    clientId: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "",
  },

  // Admin Configuration
  adminEmail: process.env.NEXT_PUBLIC_ADMIN_EMAIL || "admin@example.com",

  // Environment Info
  isDevelopment: process.env.NODE_ENV !== "production",
  isProduction: process.env.NODE_ENV === "production",
  isTest: process.env.NODE_ENV === "test",

  // Chat features and flags
  chat: {
    features: {
      branchesRefactorV2: true,
    },
  },
};

/**
 * Utility functions for API configuration
 */
export const apiConfigUtils = {
  /**
   * Get base API URL
   */
  getBaseUrl: () => apiConfig.baseUrl,

  /**
   * Get full URL for an endpoint
   */
  getEndpointUrl: (endpoint: string) => apiConfig.getEndpointUrl(endpoint),

  /**
   * Get streaming endpoint URL
   */
  getStreamingEndpointUrl: (endpoint: string = "") =>
    apiConfig.getStreamingEndpointUrl(endpoint),

  /**
   * Refresh API configuration (useful for testing)
   */
  refresh: () => apiConfig.refreshBaseUrl(),

  /**
   * Get debug information
   */
  getDebugInfo: () => apiConfig.getDebugInfo(),
};

/**
 * Validates that all required configuration is present
 * Call this during app initialization to fail fast if config is missing
 */
export function validateConfig(): void {
  const required = [{ key: "apiUrl", value: appConfig.apiUrl }];

  const missing = required.filter(({ value }) => !value || value === "");

  if (missing.length > 0) {
    const missingKeys = missing.map(({ key }) => key).join(", ");
    const error = `❌ Missing required configuration: ${missingKeys}`;

    // eslint-disable-next-line no-console
    console.error(error);

    if (appConfig.isProduction) {
      throw new Error(`Configuration validation failed: ${error}`);
    }
  }
}

export default appConfig;

// Export the ApiConfigService for advanced use cases
export { apiConfig as ApiConfig };
